/**
 * Module: App | Maintainer: Gaurav Kumar
 * Main application component for initializing the React app.
 *
 * This component handles authentication using MSAL (Microsoft Authentication Library)
 * and applies theming through Chakra UI. It conditionally renders either the authenticated
 * content (`AppInner`) or the authentication flow based on the session state.
 *
 * It also integrates external services like Datadog RUM for user monitoring,
 * and sets up React Query's `QueryClient` for API data fetching.
 *
 * Wraps Application theming, authentication, and routing.
 *
 * @returns {JSX.Element} The root application Component
 */

// Initialize AG Grid modules and license (must be before any AG Grid components)
import 'watchtower-ui/lib/agGrid';

import { InteractionStatus, InteractionType } from '@azure/msal-browser';
import { MsalAuthenticationTemplate, UnauthenticatedTemplate, useIsAuthenticated, useMsal } from '@azure/msal-react';
import type { IMsalContext } from '@azure/msal-react';
import { ChakraProvider } from '@chakra-ui/react';
import { datadogRum } from '@datadog/browser-rum';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import { lazy, Suspense, useEffect, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';

import { AUTOMATION_TOKEN } from 'watchtower-ui/api/api';
import { FullScreenLoader } from 'watchtower-ui/components/common/Spinner';
import { useInitialRootStore, useStores } from 'watchtower-ui/models/store/use-store';
import router from 'watchtower-ui/routes';
import theme from 'watchtower-ui/theme';

import 'watchtower-ui/styles/index.scss';

const Unauthorized = lazy(() => import('watchtower-ui/pages/Unauthorized/Unauthorized'));

const queryClient = new QueryClient();

const AppInner = () => (
  <QueryClientProvider client={queryClient}>
    <Suspense fallback={<FullScreenLoader full />}>
      <RouterProvider router={router} />
    </Suspense>
  </QueryClientProvider>
);

const MsalApp = observer(() => {
  const [isReady, setIsReady] = useState<boolean>(false);

  const isAuthenticated = useIsAuthenticated();
  const {
    inProgress,
    accounts: [activeAccount],
  } = useMsal();

  const {
    core: { setMsalDetails, setIsAuthenticated },
  } = useStores();

  // We give a delay of 1.5 seconds for MSAL to loadup
  useEffect(() => {
    const ti = setTimeout(() => {
      setIsReady(true);
    }, 1500);

    return () => {
      clearTimeout(ti);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setMsalDetails(null);
      setIsAuthenticated(false);
      return;
    }

    setMsalDetails({
      inProgress,
      accounts: [activeAccount],
    } as IMsalContext);
    setIsAuthenticated(true);
  }, [inProgress, activeAccount, isAuthenticated, setMsalDetails, setIsAuthenticated]);

  useEffect(() => {
    if (!import.meta.env.DEV) {
      if (activeAccount) {
        datadogRum.setUser({ email: activeAccount.username, id: activeAccount.username, name: activeAccount.name });
      } else {
        datadogRum.clearUser();
      }
    }
  }, [activeAccount]);

  // Use MsalAuthenticationTemplate for all routes
  return (
    <>
      <MsalAuthenticationTemplate
        interactionType={InteractionType.Redirect}
        authenticationRequest={{}}
        errorComponent={() => <Unauthorized />}
      >
        <FullScreenLoader full hide={isReady} />
        <AppInner />
      </MsalAuthenticationTemplate>
      {inProgress === InteractionStatus.None && !isAuthenticated && (
        <UnauthenticatedTemplate>
          <Unauthorized />
        </UnauthenticatedTemplate>
      )}
    </>
  );
});

const App = () => {
  const { rehydrated } = useInitialRootStore(() => {
    // App ready
  });

  if (!rehydrated) {
    return <FullScreenLoader full />;
  }

  return (
    <ChakraProvider theme={theme}>
      {sessionStorage.getItem(AUTOMATION_TOKEN) ? <AppInner /> : <MsalApp />}
      <ToastContainer />
    </ChakraProvider>
  );
};

App.displayName = 'App';
export default App;
